// SPDX-License-Identifier: Elastic-2.0
/**
 * PairedDevicesSection — Settings section listing every Mirlai box
 * paired to this account (T-028). At MVP this is exactly one entry
 * (the local Pi); multi-home is Pro Wave 6 territory.
 *
 * F-300 does NOT yet expose a paired-devices endpoint, so the list
 * comes from `usePairedDevices()` (T-028's localStorage stub). The
 * synthetic "This Pi" row gets a badge so the user sees which entry
 * represents the box they're currently looking at.
 *
 * Date formatting:
 *   - `last_seen` / `paired_at` are ISO strings; we render them via
 *     `Intl.DateTimeFormat` using the active i18next locale so dates
 *     match the user's regional format. Falls back to a plain string
 *     on parse failure (defensive — server data should always be ISO).
 */
import { useTranslation } from "react-i18next";

import { Card } from "@/components/Card/Card";

import { usePairedDevices } from "../useSettings";
import type { PairedDevice } from "../useSettings";
import styles from "./PairedDevicesSection.module.css";

function formatDate(iso: string, locale: string): string {
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  } catch {
    return iso;
  }
}

export function PairedDevicesSection(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = usePairedDevices();

  return (
    <Card as="section" variant="elevated" padding="md" data-testid="settings-paired-devices">
      <h2 className={styles.title}>{t("settings.paired_devices.section_title")}</h2>

      {isLoading ? (
        <p className={styles.loading} data-testid="settings-paired-devices-loading">
          {t("settings.paired_devices.loading")}
        </p>
      ) : data === undefined || data.length === 0 ? (
        <p className={styles.empty} data-testid="settings-paired-devices-empty">
          {t("settings.paired_devices.empty")}
        </p>
      ) : (
        <ul className={styles.list}>
          {data.map((device: PairedDevice, idx) => (
            <li
              key={`${device.hostname}-${idx}`}
              className={styles.row}
              data-testid={`settings-paired-device-${idx}`}
            >
              <div className={styles.rowMain}>
                <span className={styles.hostname}>{device.hostname}</span>
                {idx === 0 ? (
                  <span className={styles.badge} data-testid="settings-paired-this-pi">
                    {t("settings.paired_devices.this_pi")}
                  </span>
                ) : null}
              </div>
              <div className={styles.rowMeta}>
                <span>
                  {t("settings.paired_devices.last_seen", {
                    when: formatDate(device.last_seen, i18n.language),
                  })}
                </span>
                <span aria-hidden="true">·</span>
                <span>
                  {t("settings.paired_devices.paired", {
                    when: formatDate(device.paired_at, i18n.language),
                  })}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export default PairedDevicesSection;
